import { useTranslation } from '@jq4race/i18n';
import type { AssignedUserResponse } from '@jq4race/shared';
import {
  Badge,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
} from '@jq4race/ui';
import { Users } from 'lucide-react';

interface AssignedUsersTableProps {
  users: AssignedUserResponse[];
  loading: boolean;
  onRemove: (user: AssignedUserResponse) => void;
}

const rowActionClass =
  'inline-flex h-8 items-center justify-center rounded border border-jq-danger/30 bg-jq-danger/5 px-3 text-[12px] font-medium text-jq-danger transition hover:bg-jq-danger/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/60';

export function AssignedUsersTable({ users, loading, onRemove }: AssignedUsersTableProps) {
  const { t, i18n } = useTranslation('location-assignments');

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('table.headers.email')}</TableHead>
          <TableHead>{t('table.headers.role')}</TableHead>
          <TableHead>{t('table.headers.assignedOn')}</TableHead>
          <TableHead className="w-[120px] text-right">{t('table.headers.actions')}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {loading && users.length === 0 ? (
          <SkeletonRows />
        ) : users.length === 0 ? (
          <TableEmpty colSpan={4}>
            <EmptyState />
          </TableEmpty>
        ) : (
          users.map((user) => (
            <TableRow key={user.id}>
              <TableCell>
                <span className="font-medium text-jq-charcoal">{user.email}</span>
              </TableCell>
              <TableCell>
                <Badge variant={user.active ? 'success' : 'secondary'}>
                  {t(`roles.${user.role}`)}
                </Badge>
              </TableCell>
              <TableCell className="text-jq-graphite">
                {new Date(user.assignedAt).toLocaleDateString(i18n.language, {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })}
              </TableCell>
              <TableCell className="text-right">
                <button
                  type="button"
                  onClick={() => onRemove(user)}
                  className={rowActionClass}
                  aria-label={t('table.removeAria', { email: user.email })}
                >
                  {t('table.remove')}
                </button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
        <TableRow key={`skeleton-${i}`}>
          {Array.from({ length: 4 }).map((_, j) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: skeletons are positional
            <TableCell key={`cell-${j}`}>
              <Skeleton className="h-4 w-full" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

function EmptyState() {
  const { t } = useTranslation('location-assignments');
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Users className="h-10 w-10 text-jq-stone" strokeWidth={1.25} />
      <p className="text-[14px] font-medium text-jq-charcoal">{t('table.empty.title')}</p>
      <p className="max-w-sm text-[13px] text-jq-graphite">{t('table.empty.description')}</p>
    </div>
  );
}
